<script lang="ts">
  import { app } from "../../state/app.svelte";
  import { i18n } from "../../i18n/i18n.svelte";

  const fr = {
    close: "Fermer",
    title: "Nouveau : un seul modèle, deux modes",
    dontShowAgain: "Ne plus afficher au démarrage",
    understood: "J'ai compris",
  };
  const en: typeof fr = {
    close: "Close",
    title: "New: one model, two modes",
    dontShowAgain: "Don't show at startup again",
    understood: "Got it",
  };
  const t = $derived(i18n.locale === "en" ? en : fr);

  // Annonce de changement de modèle, affichée au démarrage (cf.
  // app.openModelNoticeOnStartup). Fermer sans cocher la case la fait revenir
  // au prochain démarrage.
  let dontShowAgain = $state(false);

  function close() {
    void app.closeModelNotice(dontShowAgain);
  }

  function onKeydown(e: KeyboardEvent) {
    if (app.modelNoticeOpen && e.key === "Escape") close();
  }
</script>

<svelte:window onkeydown={onKeydown} />

{#if app.modelNoticeOpen}
  <div
    class="backdrop"
    onclick={close}
    onkeydown={() => {}}
    role="button"
    tabindex="-1"
    aria-label={t.close}
  >
    <div
      class="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="model-notice-title"
      onclick={(e) => e.stopPropagation()}
      onkeydown={() => {}}
      tabindex="-1"
    >
      <h2 id="model-notice-title">{t.title}</h2>
      {#if i18n.locale === "en"}
        <p>
          The assistant now uses a single model, <strong>GLM-5.3-Flash</strong>.
          The Standard / Large choice, Ultra mode and the "Iterative" toggle
          are gone.
        </p>
        <p>
          Instead, there is the <span class="pill">Thinking</span> toggle below
          the input box. <strong>Off</strong>: one search across your sources,
          then the answer — fast, and just as good for reading your documents.
          <strong>On</strong>: the assistant becomes an agent that chooses its
          own searches, runs them again if needed, can read a whole document,
          and thinks before answering.
        </p>
        <p>
          In both modes, the "Assistant activity" panel shows what it is doing:
          each search with its exact query, and its reasoning.
        </p>
      {:else}
        <p>
          L'assistant utilise désormais un seul modèle, <strong>GLM-5.3-Flash</strong>.
          Le choix Standard / Large, le mode Ultra et la bulle « Itératif »
          disparaissent.
        </p>
        <p>
          À la place, la bulle <span class="pill">Réflexion</span> sous la zone de
          saisie. <strong>Éteinte</strong> : une recherche dans vos sources, puis la
          réponse — rapide, et aussi bon pour lire vos documents.
          <strong>Allumée</strong> : l'assistant devient un agent qui choisit
          lui-même ses recherches, les relance si besoin, peut lire un document
          en entier, et réfléchit avant de répondre.
        </p>
        <p>
          Dans les deux modes, l'encadré « Activité de l'assistant » montre ce
          qu'il fait : chaque recherche avec sa requête exacte, et sa réflexion.
        </p>
      {/if}
      <div class="actions">
        <label class="dont-show">
          <input type="checkbox" bind:checked={dontShowAgain} />
          {t.dontShowAgain}
        </label>
        <button class="primary" onclick={close}>{t.understood}</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 110;
    padding: 1rem;
    animation: fade 0.12s ease-out;
  }

  .modal {
    background: var(--epfl-white);
    border-radius: var(--epfl-radius-lg);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);
    max-width: 420px;
    width: 100%;
    padding: 1.1rem 1.2rem 1rem;
    animation: slide 0.15s ease-out;
  }

  h2 {
    margin: 0 0 0.6rem;
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--epfl-text);
  }

  .dont-show {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.8rem;
    color: var(--epfl-gray-600);
    cursor: pointer;
  }

  .dont-show input {
    margin: 0;
  }

  p {
    margin: 0 0 0.6rem;
    font-size: 0.85rem;
    line-height: 1.45;
    color: var(--epfl-gray-700);
  }

  .pill {
    display: inline-block;
    padding: 0.05rem 0.45rem;
    border: 1px solid var(--epfl-gray-200);
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 500;
    color: var(--epfl-gray-600);
  }

  p strong {
    color: var(--epfl-text);
  }

  .actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-top: 0.9rem;
  }

  button {
    padding: 0.45rem 0.9rem;
    border-radius: var(--epfl-radius);
    font-size: 0.82rem;
    font-family: inherit;
    cursor: pointer;
    border: 1px solid transparent;
  }

  .primary {
    background: var(--epfl-accent);
    color: var(--epfl-white);
  }

  .primary:hover {
    background: var(--epfl-accent-dark);
  }


  @keyframes fade {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slide {
    from {
      transform: translateY(12px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
</style>
